import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { compose, findIndex, includes } from 'ramda';
import styled from 'styled-components/macro';
import {
  Typography,
  Stepper,
  StepProgress,
  StepState,
} from '@deeptrue-frontend/ui';

import StoreProvider from 'components/StoreProvider/StoreProvider';
import { useFormatMessage } from 'components/IntlProvider';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import { checkAuthorize, PROJECT_TABS } from './menuSchema';
import { ReactComponent as InfoIcon } from 'assets/icons/info2.svg';

const { connect, selectors } = StoreProvider;
const { withRouter } = RouterProvider;

const NotificationBar = styled.div`
  height: 3.5rem;
  background-color: #f0f5ff;
  margin-left: -2rem;
  width: calc(100% + 4rem);
  display: flex;
  align-items: center;
  padding: 2rem;
`;

const MenuWrapper = styled.div<{ isBexioUser: boolean }>`
  border-bottom: 1px solid #e6e6e5;
  margin-top: ${({ isBexioUser }) => (isBexioUser ? '2rem' : null)};
  margin-left: -2rem;
  width: calc(100% + 6rem);
`;

type Step = {
  name: string;
  url: string;
  title: string;
  disabled: boolean;
  number: number;
  progress: StepProgress;
  state: StepState;
};

interface CustomRouterProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  id: number;
  isAdmin: boolean;
  projectStatus?: {
    value: number;
    display: string;
  };
  isBexioUser: boolean;
  isProjectSubscribable?: boolean;
  isBrandValuatorProduct: boolean;
  isProjectLoaded: boolean;
}

const ProjectMenu = ({
  history,
  location,
  id,
  isAdmin,
  match: { params },
  projectStatus,
  isBexioUser,
  isProjectSubscribable,
  isBrandValuatorProduct,
  isProjectLoaded,
}: CustomRouterProps) => {
  const t = useFormatMessage();
  const checkStepProgress = (stepNumber: number) => {
    if (projectStatus) {
      switch (projectStatus.display) {
        case 'COLLECTING_DATA':
          switch (stepNumber) {
            case 1:
              return StepProgress.COMPLETED;
            case 2:
            case 3:
            case 4:
              return StepProgress.IN_PROGRESS;
            default:
              return StepProgress.NOT_STARTED;
          }
        case 'ANALYSIS_DONE':
          return StepProgress.COMPLETED;

        case 'FINISHED':
          switch (stepNumber) {
            case 1:
            case 2:
            case 3:
            case 4:
              return StepProgress.COMPLETED;
            default:
              return StepProgress.NOT_STARTED;
          }
        case 'OFFER_DRAFT':
        case 'OFFER_ACCEPTED':
        default:
          switch (stepNumber) {
            case 1:
              return StepProgress.COMPLETED;
            case 2:
            case 3:
            case 4:
            default:
              return StepProgress.NOT_STARTED;
          }
      }
    } else {
      return StepProgress.NOT_STARTED;
    }
  };

  const checkProgressState = () => {
    switch (projectStatus?.display) {
      case 'FINISHED':
      case 'COLLECTING_DATA':
      case 'ARCHIVED':
      case 'ANALYSIS_DONE':
        return StepState.INACTIVE;
      case 'OFFER_DRAFT':
      case 'OFFER_ACCEPTED':
      case 'SUSPENDED':
      default:
        return StepState.DISABLED;
    }
  };

  const filterSteps = (step: Step) => {
    if (!isAdmin) {
      if (includes(step.name, ['files'])) {
        return false;
      }
    }
    if (step.disabled) {
      return false;
    }
    return true;
  };

  const steps = [
    {
      name: 'test & edit',
      url: params.codename
        ? `/admin/organizations/${params.codename}/projects/${id}/review`
        : `/project/${id}/review`,
      title: t('pages.project.briefingReview', 'Setup'),
      disabled: !checkAuthorize({
        isAdmin,
        tabName: PROJECT_TABS.TEST_EDIT,
      }),
      number: 1,
      progress: checkStepProgress(1),
      state: StepState.INACTIVE,
    },
    {
      name: PROJECT_TABS.COLLECT_DATA,
      url: params.codename
        ? `/admin/organizations/${params.codename}/projects/${id}/collect-data`
        : `/project/${id}/collect-data`,
      title: t('pages.project.collectData', 'Data collection'),
      disabled: !checkAuthorize({
        isAdmin,
        tabName: PROJECT_TABS.COLLECT_DATA,
        isException: isProjectSubscribable || isBrandValuatorProduct,
      }),
      number: 2,
      progress: checkStepProgress(2),
      state: StepState.INACTIVE,
    },
    {
      name: 'progress',
      url: params.codename
        ? `/admin/organizations/${params.codename}/projects/${id}/statistics`
        : `/project/${id}/statistics`,
      title: t('pages.project.progress', 'Progress'),
      disabled: !checkAuthorize({
        isAdmin,
        tabName: PROJECT_TABS.PROGRESS,
        isException: isBrandValuatorProduct,
      }),
      number: isProjectSubscribable ? 2 : 3,
      progress: checkStepProgress(3),
      state: checkProgressState(),
      isException: isBrandValuatorProduct,
    },
    {
      name: 'reports',
      url: params.codename
        ? `/admin/organizations/${params.codename}/projects/${id}/reports`
        : `/project/${id}/reports`,
      title: t('pages.project.reports', 'Reports'),
      disabled: !checkAuthorize({
        isAdmin,
        tabName: PROJECT_TABS.REPORTS,
      }),
      number: isProjectSubscribable ? 3 : isBrandValuatorProduct ? 2 : 4,
      progress: checkStepProgress(4),
      state: checkProgressState(),
      isException: isProjectSubscribable || isBrandValuatorProduct,
    },
    {
      name: 'files',
      url: params.codename
        ? `/admin/organizations/${params.codename}/projects/${id}/files`
        : `/project/${id}/files`,
      title: t('pages.project.files', 'Files'),
      disabled: !checkAuthorize({
        isAdmin,
        tabName: PROJECT_TABS.FILES,
      }),
      number: 5,
      progress: checkStepProgress(4),
      state: checkProgressState(),
      isException: isProjectSubscribable,
    },
    {
      name: 'subscriptions',
      url: params.codename
        ? `/admin/organizations/${params.codename}/projects/${id}/subscriptions`
        : `/project/${id}/subscriptions`,
      title: t('pages.project.subscriptions', 'Subscriptions'),
      disabled: !checkAuthorize({
        isAdmin,
        tabName: PROJECT_TABS.SUBSCRIPTIONS,
        isException: !isProjectSubscribable,
      }),
      number: 6,
      progress: checkStepProgress(1),
      state: StepState.INACTIVE,
    },
  ].filter(filterSteps);

  const checkPathname = (pathname: string, steps: Step[]) => {
    const itemIndex = findIndex(({ url }: { url: string }) =>
      new RegExp(url).test(pathname)
    )(steps);

    return itemIndex || 0;
  };

  return (
    <MenuWrapper isBexioUser={isBexioUser}>
      {isProjectLoaded && (
        <Stepper
          steps={steps}
          selectedIndex={checkPathname(location.pathname, steps)}
          onSelect={(stepNumber: number) => {
            if (!steps[stepNumber].disabled) {
              history.push(steps[stepNumber].url);
            }
          }}
        />
      )}
      {isBexioUser && (
        <NotificationBar>
          <InfoIcon width="1rem" />
          <Typography ml="0.5rem" variant="copy2">
            {t(
              'project.menu.notification.bexio.emails',
              'Please verify that your bexio contacts have an email set, if they are missing we will not be able to send invitations.'
            )}
          </Typography>
        </NotificationBar>
      )}
    </MenuWrapper>
  );
};

export default compose(
  withRouter,
  connect((state) => ({
    isAdmin: selectors.authSelectors.isDeepTrueAdmin(state),
    isBexioUser: selectors.authSelectors.isBexioUser(state),
    isBrandValuatorProduct: selectors.products.isBrandValuatorProduct(state),
  }))
)(ProjectMenu);
